import { MovementSensitivity } from "@drillflo/drillcore/lib/2d/Engine";
import { ViewOptions } from "@drillflo/drillcore/lib/2d/View";
import Drill from "@drillflo/drillcore/lib/Drill";
import { analyzeSpacing } from "../../services/spacingHelpers";
import Performer from "@drillflo/drillcore/lib/Performer";
import { StepPosition } from "@drillflo/drillcore/lib/Page";

export interface PerformerManipulationProps {
  drill: Drill | null;
  viewOptions: ViewOptions;
}

export default function usePerformerManipulation({
  drill,
  viewOptions,
}: PerformerManipulationProps) {
  const alignPerformersToLeft = () => {
    if (
      !drill ||
      !drill.engine ||
      !drill.engine.selectedPerformers ||
      drill.engine.selectedPerformers.length === 0
    )
      return;

    // Get the current page's performer positions
    const currentPage = drill.pages[drill.selectedPageIndex];
    if (!currentPage || !currentPage.performerPositions) return;
    const pagePerformers = currentPage.performerPositions;

    // Find the minimum horizontal position of the selected performers
    const minX = drill.engine.selectedPerformers
      .map((performerIndex) => {
        const position = pagePerformers.get(performerIndex);
        return position ? position.horizontalStepsFrom50 : Infinity;
      })
      .reduce((min, x) => Math.min(min, x), Infinity);

    if (minX === Infinity) return;

    // Align selected performers to the minimum x position
    drill.engine.selectedPerformers.forEach((performerIndex) => {
      const position = pagePerformers.get(performerIndex);
      if (position) {
        // Create a new position object with updated horizontalStepsFrom50
        const updatedPosition = {
          ...position,
          horizontalStepsFrom50: minX,
        };
        // Update the position in the Map
        pagePerformers.set(performerIndex, updatedPosition);
      }
    });
    drill.draw();

    // Trigger a redraw if needed
    if (drill) {
      drill.draw();
    }
  };

  const alignPerformersToTop = () => {
    if (
      !drill ||
      !drill.engine ||
      !drill.engine.selectedPerformers ||
      drill.engine.selectedPerformers.length === 0
    )
      return;

    // Get the current page's performer positions
    const currentPage = drill.pages[drill.selectedPageIndex];
    if (!currentPage || !currentPage.performerPositions) return;
    const pagePerformers = currentPage.performerPositions;

    const maxY = drill.engine.selectedPerformers
      .map((performerIndex) => {
        const position = pagePerformers.get(performerIndex);
        return position ? position.verticalStepsFromCenter : -Infinity;
      })
      .reduce((max, y) => Math.max(max, y), -Infinity);

    if (maxY === -Infinity) return;

    // Align selected performers to the top position
    drill.engine.selectedPerformers.forEach((performerIndex) => {
      const position = pagePerformers.get(performerIndex);
      if (position) {
        pagePerformers.set(performerIndex, {
          ...position,
          verticalStepsFromCenter: maxY,
        });
      }
    });

    drill.draw();

    // Trigger a redraw if needed
  };
  const calculateCenter = (positions, selectedPerformers) => {
    // console.log("Positions Map:", Array.from(positions.entries()));
    // console.log("Selected Performers:", selectedPerformers);

    const validPositions = selectedPerformers
      .map((index) => {
        const position = positions.get(index);
        if (position) {
          return {
            horizontalStepsFromCenter:
              Number(position.horizontalStepsFrom50) || 0,
            verticalStepsFromCenter:
              Number(position.verticalStepsFromCenter) || 0,
          };
        }
        return undefined;
      })
      .filter((pos) => pos !== undefined);

    // console.log("Valid Positions:", validPositions);

    if (validPositions.length === 0) {
      console.warn("No valid positions found. Returning default center.");
      return { horizontalStepsFromCenter: 0, verticalStepsFromCenter: 0 };
    }

    const sumX = validPositions.reduce(
      (sum, pos) =>
        sum +
        (isNaN(pos.horizontalStepsFromCenter)
          ? 0
          : pos.horizontalStepsFromCenter),
      0
    );
    const sumY = validPositions.reduce(
      (sum, pos) =>
        sum +
        (isNaN(pos.verticalStepsFromCenter) ? 0 : pos.verticalStepsFromCenter),
      0
    );

    // console.log("SumX:", sumX, "SumY:", sumY);

    return {
      horizontalStepsFromCenter: sumX / validPositions.length,
      verticalStepsFromCenter: sumY / validPositions.length,
    };
  };
  const rotatePoint = (point, center, angleRadians) => {
    const dx = point.horizontalStepsFrom50 - center.horizontalStepsFromCenter;
    const dy = point.verticalStepsFromCenter - center.verticalStepsFromCenter;
    // console.log("New is ", dx, dy);
    const cos = Math.cos(angleRadians);
    const sin = Math.sin(angleRadians);

    return {
      horizontalStepsFrom50:
        center.horizontalStepsFromCenter + (dx * cos - dy * sin),
      verticalStepsFromCenter:
        center.verticalStepsFromCenter + (dx * sin + dy * cos),
    };
  };

  const handleRotateClockwise = () => {
    if (
      !drill ||
      !drill.engine ||
      !drill.engine.selectedPerformers ||
      drill.engine.selectedPerformers.length === 0
    )
      return;
    const clockwise = true;


    const shouldSnaptoSteps = viewOptions.snapToSteps;
    const currentPage = drill.pages[drill.selectedPageIndex];
    if (!currentPage || !currentPage.performerPositions) return;
    const pagePerformers = currentPage.performerPositions;


    // Calculate center of selected performers
    const center = calculateCenter(
      pagePerformers,
      drill.engine.selectedPerformers
    );
    // console.log("Center", center);

    // 45 degrees in radians
    const angleRadians = (Math.PI / 4) * (clockwise ? -1 : 1);

    // Rotate each selected performer
    drill.engine.selectedPerformers.forEach((performerIndex) => {
      const position = pagePerformers.get(performerIndex);
      // console.log("Position is ", position);
      if (position) {
        const rotated = rotatePoint(position, center, angleRadians);
        // console.log("Rotation Would Be", rotated);
        pagePerformers.set(performerIndex, rotated);
      }
    });


    drill.draw();

  };
  const handleRotateCounterClockwise = () => {
    if (
      !drill ||
      !drill.engine ||
      !drill.engine.selectedPerformers ||
      drill.engine.selectedPerformers.length === 0
    )
      return;
    const clockwise = false;


    const shouldSnaptoSteps = viewOptions.snapToSteps;
    const currentPage = drill.pages[drill.selectedPageIndex];
    if (!currentPage || !currentPage.performerPositions) return;
    const pagePerformers = currentPage.performerPositions;


    // Calculate center of selected performers
    const center = calculateCenter(
      pagePerformers,
      drill.engine.selectedPerformers
    );
    // console.log("Center", center);

    // 45 degrees in radians
    const angleRadians = (Math.PI / 4) * (clockwise ? -1 : 1);

    // Rotate each selected performer
    drill.engine.selectedPerformers.forEach((performerIndex) => {
      const position = pagePerformers.get(performerIndex);
      // console.log("Position is ", position);
      if (position) {
        const rotated = rotatePoint(position, center, angleRadians);
        // console.log("Rotation Would Be", rotated);
        pagePerformers.set(performerIndex, rotated);
      }
    });


    drill.draw();
  };
  const alignPerformersHorizontally = () => {
    if (
      !drill ||
      !drill.engine ||
      !drill.engine.selectedPerformers ||
      drill.engine.selectedPerformers.length === 0
    )
      return;

    // Get the current page's performer positions
    const currentPage = drill.pages[drill.selectedPageIndex];
    if (!currentPage || !currentPage.performerPositions) return;
    const pagePerformers = currentPage.performerPositions;

    // Calculate the average vertical position
    const verticalPositions = drill.engine.selectedPerformers
      .map((performerIndex) => {
        const position = pagePerformers.get(performerIndex);
        return position ? position.verticalStepsFromCenter : null;
      })
      .filter((y): y is number => y !== null);

    if (verticalPositions.length === 0) return;

    const averageY =
      verticalPositions.reduce((sum, y) => sum + y, 0) /
      verticalPositions.length;

    // Align all selected performers to the average vertical position
    drill.engine.selectedPerformers.forEach((performerIndex) => {
      const position = pagePerformers.get(performerIndex);
      if (position) {
        pagePerformers.set(performerIndex, {
          ...position,
          verticalStepsFromCenter: averageY,
        });
      }
    });

    drill.draw();
  };
  const alignPerformersToBottom = () => {
    if (
      !drill ||
      !drill.engine ||
      !drill.engine.selectedPerformers ||
      drill.engine.selectedPerformers.length === 0
    )
      return;

    // Get the current page's performer positions
    const currentPage = drill.pages[drill.selectedPageIndex];
    if (!currentPage || !currentPage.performerPositions) return;
    const pagePerformers = currentPage.performerPositions;

    // Find the minimum horizontal position of the selected performers
    const maxY = drill.engine.selectedPerformers
      .map((performerIndex) => {
        const position = pagePerformers.get(performerIndex);
        return position ? position.verticalStepsFromCenter : Infinity;
      })
      .reduce((max, y) => Math.min(max, y), Infinity);

    if (maxY === Infinity) return;

    // Align selected performers to the minimum x position
    drill.engine.selectedPerformers.forEach((performerIndex) => {
      const position = pagePerformers.get(performerIndex);
      if (position) {
        // Create a new position object with updated horizontalStepsFrom50
        const updatedPosition = {
          ...position,
          verticalStepsFromCenter: maxY,
        };
        // Update the position in the Map
        pagePerformers.set(performerIndex, updatedPosition);
      }
    });
    drill.draw();

    // Trigger a redraw if needed
  };
  const alignPerformersToCenter = () => {
    if (
      !drill ||
      !drill.engine ||
      !drill.engine.selectedPerformers ||
      drill.engine.selectedPerformers.length === 0
    )
      return;

    // Get the current page's performer positions
    const currentPage = drill.pages[drill.selectedPageIndex];
    if (!currentPage || !currentPage.performerPositions) return;
    const pagePerformers = currentPage.performerPositions;

    // Calculate the average horizontal position
    const horizontalPositions = drill.engine.selectedPerformers
      .map((performerIndex) => {
        const position = pagePerformers.get(performerIndex);
        return position ? position.horizontalStepsFrom50 : null;
      })
      .filter((x): x is number => x !== null);

    if (horizontalPositions.length === 0) return;

    const averageX =
      horizontalPositions.reduce((sum, x) => sum + x, 0) /
      horizontalPositions.length;

    // Align all selected performers to the average vertical position
    drill.engine.selectedPerformers.forEach((performerIndex) => {
      const position = pagePerformers.get(performerIndex);
      if (position) {
        pagePerformers.set(performerIndex, {
          ...position,
          horizontalStepsFrom50: averageX,
        });
      }
    });

    // Clear rotation state and reset movement sensitivity
    drill.engine.movementSensitivity = viewOptions.snapToSteps
      ? drill.engine.movementSensitivity === MovementSensitivity.HALF_STEP
        ? MovementSensitivity.HALF_STEP
        : MovementSensitivity.FULL_STEP
      : MovementSensitivity.FREE;
    drill.engine.endRotation();
    drill.engine.selectPerformers([]);

    drill.draw();
  };

  const alignPerformersToRight = () => {
    if (
      !drill ||
      !drill.engine ||
      !drill.engine.selectedPerformers ||
      drill.engine.selectedPerformers.length === 0
    )
      return;

    // Get the current page's performer positions
    const currentPage = drill.pages[drill.selectedPageIndex];
    if (!currentPage || !currentPage.performerPositions) return;
    const pagePerformers = currentPage.performerPositions;

    // Find the maximum horizontal position of the selected performers
    const maxX = drill.engine.selectedPerformers
      .map((performerIndex) => {
        const position = pagePerformers.get(performerIndex);
        return position ? position.horizontalStepsFrom50 : -Infinity;
      })
      .reduce((max, x) => Math.max(max, x), -Infinity);

    if (maxX === -Infinity) return;

    // Align selected performers to the maximum x position
    drill.engine.selectedPerformers.forEach((performerIndex) => {
      const position = pagePerformers.get(performerIndex);
      if (position) {
        pagePerformers.set(performerIndex, {
          ...position,
          horizontalStepsFrom50: maxX,
        });
      }
    });

    drill.draw();
    drill.engine.clearSelection();
  };
  const handleNudgeRight = () => {
    if (
      !drill ||
      !drill.engine ||
      !drill.engine.selectedPerformers ||
      drill.engine.selectedPerformers.length === 0
    )
      return;

    drill.engine.selectedPerformers.forEach((performer) => {
      const position = performer.position;
      if (position && drill.engine) {
        const stepSize = drill.engine.movementSensitivity === 1 ? 0.5 : 1;

        const newPosition = position.horizontalStepsFrom50 + stepSize;
        const updatedPosition = {
          ...position,
          horizontalStepsFrom50: newPosition,
        };
        // Update the position in the Map
        performer.position = updatedPosition;
      }
    });
    drill.draw();
    const selectedPerformers = drill.engine.selectedPerformers;
    drill.engine.selectPerformers(selectedPerformers);
    // setRedrawNonce((prevNonce) => prevNonce + 1);
    // Trigger a redraw if needed
    if (drill) {
      drill.draw();
    }
  };
  const handleNudgeLeft = () => {
    if (
      !drill ||
      !drill.engine ||
      !drill.engine.selectedPerformers ||
      drill.engine.selectedPerformers.length === 0
    )
      return;

    drill.engine.selectedPerformers.forEach((performer) => {
      const position = performer.position;
      if (position && drill.engine) {
        const stepSize = drill.engine.movementSensitivity === 1 ? 0.5 : 1;

        const newPosition = position.horizontalStepsFrom50 - stepSize;
        const updatedPosition = {
          ...position,
          horizontalStepsFrom50: newPosition,
        };
        // Update the position in the Map
        performer.position = updatedPosition;
      }
    });
    drill.draw();
    const selectedPerformers = drill.engine.selectedPerformers;
    drill.engine.selectPerformers(selectedPerformers);
    // setRedrawNonce((prevNonce) => prevNonce + 1);
    // Trigger a redraw if needed
    if (drill) {
      drill.draw();
    }
  };
  const handleNudgeUp = () => {
    if (
      !drill ||
      !drill.engine ||
      !drill.engine.selectedPerformers ||
      drill.engine.selectedPerformers.length === 0
    )
      return;

    drill.engine.selectedPerformers.forEach((performer) => {
      const position = performer.position;
      if (position && drill.engine) {
        const stepSize = drill.engine.movementSensitivity === 1 ? 0.5 : 1;

        const newPosition = position.verticalStepsFromCenter + stepSize;
        const updatedPosition = {
          ...position,
          verticalStepsFromCenter: newPosition,
        };
        // Update the position in the Map
        performer.position = updatedPosition;
      }
    });
    drill.draw();
    const selectedPerformers = drill.engine.selectedPerformers;
    drill.engine.selectPerformers(selectedPerformers);
    // setRedrawNonce((prevNonce) => prevNonce + 1);
    // Trigger a redraw if needed
    if (drill) {
      drill.draw();
    }
  };
  const handleNudgeDown = () => {
    if (
      !drill ||
      !drill.engine ||
      !drill.engine.selectedPerformers ||
      drill.engine.selectedPerformers.length === 0
    )
      return;

    drill.engine.selectedPerformers.forEach((performer) => {
      const position = performer.position;
      if (position && drill.engine) {
        const stepSize = drill.engine.movementSensitivity === 1 ? 0.5 : 1;

        const newPosition = position.verticalStepsFromCenter - stepSize;
        const updatedPosition = {
          ...position,
          verticalStepsFromCenter: newPosition,
        };
        // Update the position in the Map
        performer.position = updatedPosition;
      }
    });
    drill.draw();
    const selectedPerformers = drill.engine.selectedPerformers;
    drill.engine.selectPerformers(selectedPerformers);
    // setRedrawNonce((prevNonce) => prevNonce + 1);
    // Trigger a redraw if needed
    if (drill) {
      drill.draw();
    }
  };

  const applyEvenSpacing = (spacing: number) => {
    console.log("applyEvenSpacing", spacing);
    if (
      !drill ||
      !drill.engine ||
      !drill.engine.selectedPerformers ||
      drill.engine.selectedPerformers.length === 0
    )
      return;

    if (spacing === 0) return;

    const currentPage = drill.pages[drill.selectedPageIndex];
    if (!currentPage || !currentPage.performerPositions) return;

    const pagePerformers = currentPage.performerPositions;
    const selectedPerformers = drill.engine.selectedPerformers;

    if (selectedPerformers.length < 2) return;
    const positions = selectedPerformers
      .map((index) => pagePerformers.get(index))
      .filter((pos): pos is NonNullable<typeof pos> => pos !== undefined);

    // Analyze current arrangement
    const analysis = analyzeSpacing(positions);
    console.log("analysis", analysis);
    if (analysis.direction === "horizontal") {
      applyHorizontalSpacing(
        selectedPerformers,
        pagePerformers,
        spacing
      );
    } else {
      applyVerticalSpacing(selectedPerformers, pagePerformers, spacing);
    }
    drill.draw();
    drill.engine.selectPerformers(selectedPerformers);
  };
  function applyHorizontalSpacing(
    selectedPerformers: Performer[],
    pagePerformers: Map<Performer, StepPosition>,
    spacing: number
  ): void {
    // Sort performers by current horizontal position
    const sortedPerformers = [...selectedPerformers].sort((a, b) => {
      const posA = pagePerformers.get(a)?.horizontalStepsFrom50 ?? 0;
      const posB = pagePerformers.get(b)?.horizontalStepsFrom50 ?? 0;
      return posA - posB;
    });

    // Get leftmost position as anchor
    const startPos =
      pagePerformers.get(sortedPerformers[0])?.horizontalStepsFrom50 ?? 0;

    // Apply new spacing
    sortedPerformers.forEach((performerIndex, i) => {
      const position = pagePerformers.get(performerIndex);
      if (position) {
        pagePerformers.set(performerIndex, {
          ...position,
          horizontalStepsFrom50: startPos + i * spacing,
        });
      }
    });
  }

  function applyVerticalSpacing(
    selectedPerformers: Performer[],
    pagePerformers: Map<Performer, StepPosition>,
    spacing: number
  ): void {
    // Sort performers by current vertical position
    const sortedPerformers = [...selectedPerformers].sort((a, b) => {
      const posA = pagePerformers.get(a)?.verticalStepsFromCenter ?? 0;
      const posB = pagePerformers.get(b)?.verticalStepsFromCenter ?? 0;
      return posA - posB;
    });

    // Get topmost position as anchor
    const startPos =
      pagePerformers.get(sortedPerformers[0])?.verticalStepsFromCenter ?? 0;

    // Apply new spacing
    sortedPerformers.forEach((performerIndex, i) => {
      const position = pagePerformers.get(performerIndex);
      if (position) {
        pagePerformers.set(performerIndex, {
          ...position,
          verticalStepsFromCenter: startPos + i * spacing,
        });
      }
    });
  }

  return {
    alignPerformersToLeft,
    alignPerformersToTop,
    alignPerformersHorizontally,
    alignPerformersToBottom,
    alignPerformersToCenter,
    alignPerformersToRight,
    handleNudgeRight,
    handleNudgeLeft,
    handleNudgeUp,
    handleNudgeDown,
    applyEvenSpacing,
    handleRotateClockwise,
    handleRotateCounterClockwise,
  };
}
